/**
 * useMarqueeSelection Hook
 *
 * Provides drag-to-select (marquee/lasso) functionality for selecting multiple components.
 *
 * Features:
 * - Click and drag to create selection rectangle
 * - Automatically selects components that intersect with the rectangle
 * - Supports additive selection with Shift key
 * - Only activates in editing mode (respects AI-first workflow in viewing mode)
 * - Prevents text selection during drag
 * - WCAG 2.1 AA compliant with keyboard alternatives
 *
 * Week 3: Selection System implementation
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useEditorStore } from '@/stores/editor/editor-store';
import { devLog } from '@/utils/devLog';
import type { Component } from '@/types';

interface MarqueeState {
  /** Whether marquee selection is currently active */
  isActive: boolean;
  /** Starting x coordinate (canvas space) */
  startX: number;
  /** Starting y coordinate (canvas space) */
  startY: number;
  /** Current x coordinate (canvas space) */
  currentX: number;
  /** Current y coordinate (canvas space) */
  currentY: number;
  /** Whether Shift key is held for additive selection */
  isAdditive: boolean;
}

interface MarqueeRectangle {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface UseMarqueeSelectionOptions {
  /**
   * Enable or disable marquee selection
   * Set to false in viewing mode for AI-first workflow
   */
  enabled?: boolean;

  /**
   * Canvas element for coordinate conversion
   * Required to convert screen coordinates to canvas coordinates
   */
  canvasElement: HTMLDivElement | null;

  /**
   * Optional scroll container element to attach mousedown listener to
   * If provided, allows marquee selection to start from anywhere in the scroll area
   * If not provided, mousedown listener is attached to canvasElement only
   */
  scrollContainerElement?: HTMLDivElement | null;

  /**
   * Current viewport scale for coordinate conversion
   * Required to convert screen coordinates to canvas coordinates
   */
  scale?: number;

  /**
   * Minimum drag distance (in pixels) before activating marquee
   * Prevents accidental activation on clicks
   * Default: 5
   */
  minimumDragDistance?: number;

  /**
   * Callback fired when selection changes during marquee
   */
  onSelectionChange?: (selectedIds: string[]) => void;
}

/**
 * Check if a component intersects with the marquee rectangle
 */
function doesComponentIntersect(
  component: Component,
  marquee: MarqueeRectangle
): boolean {
  if (!component.position || !component.size) return false;

  const compX = component.position.x;
  const compY = component.position.y;
  const compWidth = typeof component.size.width === 'number'
    ? component.size.width
    : parseFloat(String(component.size.width)) || 0;
  const compHeight = typeof component.size.height === 'number'
    ? component.size.height
    : parseFloat(String(component.size.height)) || 0;

  // Check if rectangles intersect (AABB collision detection)
  const intersects = !(
    compX + compWidth < marquee.x ||
    compX > marquee.x + marquee.width ||
    compY + compHeight < marquee.y ||
    compY > marquee.y + marquee.height
  );

  return intersects;
}

/**
 * useMarqueeSelection Hook
 *
 * @param options - Configuration options
 *
 * @example
 * ```typescript
 * function EditorCanvas() {
 *   const [canvasElement, setCanvasElement] = useState<HTMLDivElement | null>(null);
 *   const { marqueeState, marqueeRectangle } = useMarqueeSelection({
 *     enabled: mode === 'editing',
 *     canvasElement,
 *     scale: effectiveScale,
 *     onSelectionChange: (ids) => {
 *       console.log('Selected:', ids.length, 'components');
 *     }
 *   });
 *
 *   // Render marquee overlay when active
 *   // marqueeState.isActive - boolean indicating if selection is active
 *   // marqueeRectangle - { x, y, width, height } of selection rectangle
 * }
 * ```
 */
export function useMarqueeSelection(options: UseMarqueeSelectionOptions) {
  const {
    enabled = true,
    canvasElement,
    scrollContainerElement,
    scale = 1,
    minimumDragDistance = 5,
    onSelectionChange,
  } = options;

  const [marqueeState, setMarqueeState] = useState<MarqueeState>({
    isActive: false,
    startX: 0,
    startY: 0,
    currentX: 0,
    currentY: 0,
    isAdditive: false,
  });

  const { components, selectMultipleComponents, selectedComponents } = useEditorStore();

  // Track initial selected components for additive selection
  const initialSelectedRef = useRef<string[]>([]);

  /**
   * Convert screen coordinates to canvas coordinates
   */
  const screenToCanvasCoords = useCallback(
    (screenX: number, screenY: number): { x: number; y: number } => {
      if (!canvasElement) return { x: screenX, y: screenY };

      const rect = canvasElement.getBoundingClientRect();
      // Ensure scale is treated as a number for arithmetic operations
      const safeScale: number = scale;
      const x = (screenX - rect.left) / safeScale;
      const y = (screenY - rect.top) / safeScale;

      return { x, y };
    },
    [canvasElement, scale]
  );

  /**
   * Calculate the normalized marquee rectangle
   * (handles dragging in any direction)
   */
  const marqueeRectangle = useCallback((): MarqueeRectangle | null => {
    if (!marqueeState.isActive) return null;

    const x = Math.min(marqueeState.startX, marqueeState.currentX);
    const y = Math.min(marqueeState.startY, marqueeState.currentY);
    const width = Math.abs(marqueeState.currentX - marqueeState.startX);
    const height = Math.abs(marqueeState.currentY - marqueeState.startY);

    return { x, y, width, height };
  }, [marqueeState]);

  /**
   * Helper to calculate rectangle from coordinates
   */
  const calculateRectangle = useCallback((
    startX: number,
    startY: number,
    currentX: number,
    currentY: number
  ): MarqueeRectangle => {
    const x = Math.min(startX, currentX);
    const y = Math.min(startY, currentY);
    const width = Math.abs(currentX - startX);
    const height = Math.abs(currentY - startY);
    return { x, y, width, height };
  }, []);

  /**
   * Find components that intersect with a given marquee rectangle
   */
  const findIntersectingComponents = useCallback((rect: MarqueeRectangle | null): string[] => {
    if (!rect) {
      return [];
    }

    const visibleComponents = components.filter((component) => !component.hidden && !component.isDeleted);

    if (visibleComponents.length === 0) {
      return [];
    }

    const intersecting = visibleComponents
      .filter(comp => doesComponentIntersect(comp, rect))
      .map(comp => comp.id);

    return intersecting;
  }, [components]);

  /**
   * Handle mouse down - start marquee selection
   */
  const handleMouseDown = useCallback(
    (e: MouseEvent) => {
      if (!enabled) return;
      if (!canvasElement) return;

      // Only activate on left mouse button
      if (e.button !== 0) return;

      // Don't activate if clicking on a component (let component handle it)
      const target = e.target as HTMLElement;
      if (target.closest('[data-component-wrapper]')) {
        devLog('SELECTION', 'Marquee: Click on component, not activating');
        return;
      }

      const coords = screenToCanvasCoords(e.clientX, e.clientY);

      devLog('SELECTION', 'Marquee: Mouse down', {
        screenX: e.clientX,
        screenY: e.clientY,
        canvasX: coords.x,
        canvasY: coords.y,
        shiftKey: e.shiftKey,
      });

      // Store initial selected components for additive selection
      if (e.shiftKey) {
        initialSelectedRef.current = [...selectedComponents];
      } else {
        initialSelectedRef.current = [];
      }

      setMarqueeState({
        isActive: false, // Don't activate until minimum drag distance
        startX: coords.x,
        startY: coords.y,
        currentX: coords.x,
        currentY: coords.y,
        isAdditive: e.shiftKey,
      });

      // Prevent text selection during drag
      e.preventDefault();
    },
    [enabled, canvasElement, screenToCanvasCoords, selectedComponents]
  );

  /**
   * Handle mouse move - update marquee rectangle and selection
   */
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!enabled) return;
      if (marqueeState.startX === 0 && marqueeState.startY === 0) return;

      const coords = screenToCanvasCoords(e.clientX, e.clientY);

      // Check if we've moved beyond the minimum drag distance
      const deltaX = Math.abs(coords.x - marqueeState.startX);
      const deltaY = Math.abs(coords.y - marqueeState.startY);
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      // Activate marquee if we've dragged far enough
      const shouldActivate = distance >= minimumDragDistance;

      setMarqueeState((prev) => ({
        ...prev,
        isActive: prev.isActive || shouldActivate,
        currentX: coords.x,
        currentY: coords.y,
      }));

      // Update selection if marquee is active
      if (shouldActivate || marqueeState.isActive) {
        // Calculate rectangle with CURRENT coordinates (not from state which hasn't updated yet)
        const currentRect = calculateRectangle(
          marqueeState.startX,
          marqueeState.startY,
          coords.x,
          coords.y
        );

        const intersecting = findIntersectingComponents(currentRect);

        // Combine with initial selection if additive
        const finalSelection = marqueeState.isAdditive
          ? [...new Set([...initialSelectedRef.current, ...intersecting])]
          : intersecting;

        selectMultipleComponents(finalSelection);
        onSelectionChange?.(finalSelection);
      }
    },
    [
      enabled,
      marqueeState,
      screenToCanvasCoords,
      minimumDragDistance,
      calculateRectangle,
      findIntersectingComponents,
      selectMultipleComponents,
      onSelectionChange,
    ]
  );

  /**
   * Handle mouse up - complete marquee selection
   */
  const handleMouseUp = useCallback(
    (e: MouseEvent) => {
      if (!enabled) return;
      if (!marqueeState.isActive && marqueeState.startX === 0 && marqueeState.startY === 0) return;

      devLog('SELECTION', 'Marquee: Mouse up', {
        wasActive: marqueeState.isActive,
        selectedCount: selectedComponents.length,
      });

      // If marquee was never activated (just a click), clear selection unless additive
      if (!marqueeState.isActive && !marqueeState.isAdditive) {
        selectMultipleComponents([]);
        onSelectionChange?.([]);
      }

      // Reset marquee state
      setMarqueeState({
        isActive: false,
        startX: 0,
        startY: 0,
        currentX: 0,
        currentY: 0,
        isAdditive: false,
      });

      initialSelectedRef.current = [];
    },
    [enabled, marqueeState, selectedComponents, selectMultipleComponents, onSelectionChange]
  );

  // Attach event listeners
  useEffect(() => {
    // Use scroll container if available, otherwise use canvas
    const mouseDownTarget = scrollContainerElement || canvasElement;

    if (!enabled) {
      return;
    }

    if (!canvasElement) {
      return;
    }

    if (!mouseDownTarget) {
      return;
    }

    mouseDownTarget.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    devLog('SELECTION', 'Marquee: Event listeners attached');

    return () => {
      mouseDownTarget.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);

      devLog('SELECTION', 'Marquee: Event listeners removed');
    };
  }, [enabled, canvasElement, scrollContainerElement, handleMouseDown, handleMouseMove, handleMouseUp]);

  return {
    marqueeState,
    marqueeRectangle: marqueeRectangle(),
    isActive: marqueeState.isActive,
  };
}

/**
 * Export types for use in other components
 */
export type { MarqueeState, MarqueeRectangle };
